import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Subscription, finalize } from 'rxjs';

import { AbstractHypeService } from '../../../../Common/Layout/services/abstract-hype.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { SocialHypeService } from '../../../Core/services/social-hype.service';

import { Theme } from '../../models/theme.model';
import { ThemeService } from '../../services/theme.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { ArchiveSearchWidgetComponent } from '../ArchiveSearchWidget/archive-search-widget.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';

@Component({
    selector: 'agpr-theme-management',
    templateUrl: './theme-management.component.html',
    styleUrls: ['./theme-management.component.scss'],
    providers: [{
        provide: AbstractHypeService,
        useExisting: SocialHypeService
    }],
    standalone: false
})
export class ThemeManagementComponent implements OnInit, OnDestroy {

    readonly id = 'theme-management';
    readonly buttonTypes = ButtonTypesEnum;

    @ViewChild('scrollPanel') scrollPanel: ScrollPanelComponent;

    overlayContainer: Element;

    themes: Theme[] = [];

    loading: boolean = true;
    isCreatingTheme: boolean = false;

    private expandedThemesIds = new Set<number>();
    private newlyCreatedThemeIds = new Set<number>();

    private subscriptions: Subscription[] = [];

    constructor(
        private themeService: ThemeService,
        private hypeService: SocialHypeService,
        private coreDialogService: CoreDialogService
    ) {
        this.subscriptions.push(
            this.themeService.all$.subscribe(themes => {
                this.themes = themes;
                // Keep the expanded/collapsed state of theme cards
                this.expandAndReorderNewThemeToTail();
                this.loading = false;
            }),
            this.hypeService.buttonClicked$.subscribe(clicked => {
                if (this.themes.length === 0 && clicked) {
                    this.createTheme();
                }
            })
        );
    }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');
    }

    createTheme() {
        this.isCreatingTheme = true;

        const newTheme = new Theme();
        newTheme.name = this.createDefaultThemeName();

        this.themeService.create(newTheme).pipe(
            finalize(() => this.isCreatingTheme = false)
        ).subscribe((id) => {
            this.newlyCreatedThemeIds.add(id);
            this.expandedThemesIds.add(id);
            this.expandAndReorderNewThemeToTail();

            // scroll to bottom
            requestAnimationFrame(() => {
                this.scrollPanel.scrollTo(100000);
            });
        });
    }

    onDeleteTheme(theme: Theme) {
        this.expandedThemesIds.delete(theme.id);
        this.newlyCreatedThemeIds.delete(theme.id);
    }

    searchArchive() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
            },
            outputs: {}
        };
        this.coreDialogService.open(ArchiveSearchWidgetComponent, io);
    }

    onToggleExpand(isExpanded: boolean, theme: Theme) {
        theme.expanded = isExpanded;

        if (isExpanded) {
            this.expandedThemesIds.add(theme.id);
        } else {
            this.expandedThemesIds.delete(theme.id);
        }
    }



    /**
     * Expands newly created themes and puts the newest themes at the bottom
     */
    private expandAndReorderNewThemeToTail() {
        this.themes.forEach(theme => {
            theme.expanded = this.expandedThemesIds.has(theme.id);
        });

        const newThemes = this.themes
            .filter(theme => this.newlyCreatedThemeIds.has(theme.id))
            .sort((a, b) => a.createdDate.getTime() - b.createdDate.getTime());

        const oldThemes = this.themes.filter(theme => !this.newlyCreatedThemeIds.has(theme.id));

        this.themes = [...oldThemes, ...newThemes];
    }

    /**
     * Following the pattern Theme XX, return the first unused name.
     */
    private createDefaultThemeName(): string {

        // collect number from all themes that follow theme XX
        const themeNameIndices = this.themes
            .filter(theme => /^theme [0-9]+$/g.test(theme.name.trim().toLowerCase()))
            .map(theme => +theme.name.split(' ')[1]);


        let newIndex = -1;
        for (let i = 1; i <= this.themes.length; i++) {
            if (themeNameIndices.indexOf(i) === -1) {
                newIndex = i;
                break;
            }
        }
        newIndex = newIndex === -1 ? this.themes.length + 1 : newIndex;

        return `Theme ${newIndex}`;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
